import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { ChevronRight, ArrowLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMemo } from "react";
import DOMPurify from "dompurify";

/**
 * Converts markdown-style pipe tables in HTML content to proper <table> elements.
 * Handles: | col1 | col2 |\n|---|---|\n| val1 | val2 |
 */
function parseMarkdownTables(html: string): string {
  // Match blocks of lines that start/end with |
  return html.replace(
    /(<br\s*\/?>|\n)?((?:\|[^\n]+\|\s*(?:<br\s*\/?>|\n)?){2,})/gi,
    (_match, _pre, block: string) => {
      const lines = block
        .split(/<br\s*\/?>|\n/)
        .map((l) => l.trim())
        .filter((l) => l.startsWith("|") && l.endsWith("|"));

      if (lines.length < 2) return block;

      const parseCells = (line: string) =>
        line
          .slice(1, -1)
          .split("|")
          .map((c) => c.trim());

      const headerCells = parseCells(lines[0]);

      // Check if second line is separator (---)
      const isSep = parseCells(lines[1]).every((c) => /^[-:]+$/.test(c));
      const dataStart = isSep ? 2 : 1;

      let table = '<table><thead><tr>';
      headerCells.forEach((c) => (table += `<th>${c}</th>`));
      table += '</tr></thead><tbody>';

      for (let i = dataStart; i < lines.length; i++) {
        const cells = parseCells(lines[i]);
        if (cells.every((c) => /^[-:]+$/.test(c))) continue;
        table += '<tr>';
        cells.forEach((c) => (table += `<td>${c}</td>`));
        table += '</tr>';
      }

      table += '</tbody></table>';
      return table;
    }
  );
}

const HelpArticle = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading } = useQuery({
    queryKey: ["knowledge-article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("knowledge_articles")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: related = [] } = useQuery({
    queryKey: ["knowledge-related", article?.category, article?.parent_slug, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("knowledge_articles")
        .select("id, slug, title")
        .eq("category", article!.category)
        .neq("slug", slug!)
        .order("sort_order", { ascending: true })
        .limit(8);
      if (error) throw error;
      return data;
    },
    enabled: !!article,
  });

  const processedContent = useMemo(() => {
    if (!article?.content) return "";
    return DOMPurify.sanitize(parseMarkdownTables(article.content));
  }, [article?.content]);

  const metaDescription = useMemo(() => {
    if (!article?.content) return "";
    const text = article.content
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const trimmed = text.length > 155 ? text.slice(0, 152).trimEnd() + "…" : text;
    return trimmed || `Anleitung: ${article.title}.`;
  }, [article?.content, article?.title]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className="container py-20">
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="h-8 w-48 animate-pulse rounded bg-muted" />
            <div className="h-6 w-96 animate-pulse rounded bg-muted" />
            <div className="mt-8 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-muted" style={{ width: `${70 + Math.random() * 30}%` }} />
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!article) {
    return (
      <PageLayout>
        <div className="container py-20 text-center">
          <h1 className="mb-4 text-2xl font-bold text-foreground">Artikel nicht gefunden</h1>
          <Button asChild variant="outline">
            <Link to="/hilfe">Zurück zur Hilfe</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>{article.title} – NextInvoice Hilfe</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://nxtinvoice.de/hilfe/${article.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${article.title} – NextInvoice Hilfe`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://nxtinvoice.de/hilfe/${article.slug}`} />
        <meta property="og:site_name" content="NextInvoice" />
        <meta property="article:section" content={article.category} />
        <meta property="article:modified_time" content={article.updated_at} />
        <meta property="article:published_time" content={article.created_at} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} – NextInvoice Hilfe`} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: metaDescription,
            articleSection: article.category,
            datePublished: article.created_at,
            dateModified: article.updated_at,
            inLanguage: "de-DE",
            author: { "@type": "Organization", name: "NextInvoice" },
            publisher: {
              "@type": "Organization",
              name: "NextInvoice",
              logo: { "@type": "ImageObject", url: "https://nxtinvoice.de/favicon.svg" },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://nxtinvoice.de/hilfe/${article.slug}`,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://nxtinvoice.de/" },
              { "@type": "ListItem", position: 2, name: "Hilfe", item: "https://nxtinvoice.de/hilfe" },
              { "@type": "ListItem", position: 3, name: article.title, item: `https://nxtinvoice.de/hilfe/${article.slug}` },
            ],
          })}
        </script>
      </Helmet>

      {/* Header with gradient */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/5 pb-10 pt-12 lg:pt-16">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="container relative">
          {/* Breadcrumb */}
          <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Startseite</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/hilfe" className="hover:text-foreground transition-colors">Hilfe</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground font-medium">{article.title}</span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
            <div>
              <span className="mb-2 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {article.category}
              </span>
              <h1 className="font-display text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
                {article.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      <div className="container py-10 lg:py-14">
        <div className="flex flex-col gap-10 lg:flex-row">
          {/* Main Content */}
          <article className="min-w-0 flex-1">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8 lg:p-10">
              <div
                className="help-article-content prose prose-sm sm:prose-base max-w-none
                  prose-headings:font-display prose-headings:text-foreground
                  prose-p:text-muted-foreground prose-p:leading-relaxed
                  prose-li:text-muted-foreground
                  prose-strong:text-foreground
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-hr:border-border
                  prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm"
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            </div>
          </article>

          {/* Sidebar */}
          {related.length > 0 && (
            <aside className="w-full shrink-0 lg:w-72">
              <div className="sticky top-28 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-5 py-4">
                  <h3 className="font-display text-sm font-semibold text-foreground">
                    Verwandte Artikel
                  </h3>
                </div>
                <ul className="space-y-1 p-3">
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/hilfe/${r.slug}`}
                        className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-primary/5 hover:text-primary"
                      >
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>

        <div className="mt-10">
          <Button asChild variant="outline" size="sm" className="rounded-xl">
            <Link to="/hilfe">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück zur Übersicht
            </Link>
          </Button>
        </div>
      </div>
    </PageLayout>
  );
};

export default HelpArticle;
